// The way the camera faced when a photo was taken (SCHEMA.md "Before UAT" §7).
// The compass is read when the photo comes back from the camera and rounded to
// eight points; the exact bearing is kept too, for condition reports.
//
// iPad and iPhone only give the compass after the user allows it, and only when
// asked from a tap — so the first tap on a photo screen asks, once. No compass,
// or permission refused: photos simply have no direction.

import { compassPoint } from '@higgins/condition-library';

export const DIRECTIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;
export type Direction = (typeof DIRECTIONS)[number];

const DEGREES_PER_POINT = 360 / DIRECTIONS.length;
/** A reading older than this is stale (the device stopped reporting), so it isn't used. */
const FRESH_MS = 3000;

/** A compass bearing (0 = north, clockwise) to the nearest of eight points: the library's, so the report's captions match. */
export const bearingToDirection = (bearing: number): Direction => compassPoint(bearing) as Direction;

/** A point set by hand, as the bearing at its centre (N 0, NE 45 …) — how a photo's row stores it. */
export const directionToBearing = (direction: Direction): number => DIRECTIONS.indexOf(direction) * DEGREES_PER_POINT;

export function isDirection(value: unknown): value is Direction {
  return typeof value === 'string' && (DIRECTIONS as readonly string[]).includes(value);
}

interface OrientationReading extends DeviceOrientationEvent {
  /** Safari's true-north heading. */
  webkitCompassHeading?: number;
}

let latest: { bearing: number; at: number } | null = null;
let started = false;
let armed = false;

function onOrientation(event: OrientationReading) {
  // Safari gives a heading directly; elsewhere only an absolute alpha points at
  // north (a relative one is wherever the device happened to start).
  const heading = event.webkitCompassHeading ?? (event.absolute && event.alpha !== null ? 360 - event.alpha : null);
  if (heading !== null && Number.isFinite(heading)) latest = { bearing: Math.round(heading) % 360, at: Date.now() };
}

function listen() {
  if (started) return;
  started = true;
  const absoluteEvent = 'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';
  window.addEventListener(absoluteEvent, onOrientation as EventListener);
}

type PermissionAsker = { requestPermission?: () => Promise<'granted' | 'denied'> };

/**
 * Ask for the compass (iOS) on a completed tap and start reading it. iOS only
 * accepts the request from a finished tap (click), never a finger going down
 * — a request from pointerdown is refused with no prompt (2026-10-14 iPad
 * test). A tap on the camera button is skipped, so the permission prompt and
 * the camera never share one tap; a refused request waits for the next tap.
 */
function onTap(event: MouseEvent) {
  if (event.target instanceof Element && event.target.closest('label, input[type="file"]')) return;
  window.removeEventListener('click', onTap, true);
  if (typeof DeviceOrientationEvent === 'undefined') return;
  const ask = (DeviceOrientationEvent as unknown as PermissionAsker).requestPermission;
  if (typeof ask !== 'function') return listen();
  ask().then(
    (answer) => {
      if (answer === 'granted') listen();
    },
    // Not counted as a tap after all: try again on the next one.
    () => window.addEventListener('click', onTap, true),
  );
}

/** Start the compass on the next tap anywhere (not the camera button) — once per page load. */
export function armCompass(): void {
  if (armed || typeof window === 'undefined') return;
  armed = true;
  window.addEventListener('click', onTap, true);
}

/** How long to wait for the compass to report again after the camera hands back. */
const WAIT_FOR_READING_MS = 1500;
/** Failing a fresh one, the last reading before the camera opened is used if it's no older than this. */
const FALLBACK_MS = 120_000;

const isFresh = (maxAgeMs: number) => !!latest && Date.now() - latest.at <= maxAgeMs;

/**
 * The bearing for a photo just back from the camera. The page was hidden while
 * the camera was open, so the compass may not have reported yet: wait briefly
 * for it, else use the last reading from before the camera (when the measurer
 * was facing the subject), else null.
 */
export async function bearingForPhoto(): Promise<number | null> {
  if (!started) return null;
  const deadline = Date.now() + WAIT_FOR_READING_MS;
  while (!isFresh(FRESH_MS) && Date.now() < deadline) await new Promise((resolve) => setTimeout(resolve, 100));
  return isFresh(FRESH_MS) || isFresh(FALLBACK_MS) ? latest!.bearing : null;
}

/** The way the device faces now, for a live guide (the retake camera); null without a fresh reading. */
export const bearingNow = (): number | null => (isFresh(FRESH_MS) ? latest!.bearing : null);
